import pick from 'lodash/pick';

import type { IDraftFirstStepResponse } from '../../api/models';
import type { TMapFields } from '../../models';
import type { TFirstStepData, TResponseFormatData } from '../../models/data';
import { archivariusFields } from '../../constants';

import type { TDirectionSendData } from './constants';
import { mapConfigSeparatorForDate } from './constants';

/**
 * Принимает список функций в виде аргументов и возвращает функцию,
 * которая принимает некоторое значение в качестве аргумента,
 * и запускает ее через конвейер исходных функций, заданных в этой функции
 */
export const pipe =
    <T>(...fns: Array<(arg: never) => T>) =>
    (value: T) =>
        fns.reduce((acc, fn) => fn(acc as never), value);

/**
 * Преобразует дату в новый формат
 * @example date = 2020-12-01, currentSeparator = '-', separator = '.' => 01.12.2020
 */
export const formatDate = (direction: TDirectionSendData) => (data: IDraftFirstStepResponse) => {
    const { currentSeparator, separator } = mapConfigSeparatorForDate[direction];

    return {
        ...data,
        birthDate: data.birthDate?.split(currentSeparator).reverse().join(separator) ?? null,
    };
};

/**
 * Адаптирует данные в объекте исходя из схемы матчинга полей
 * mapFields = { gender: 'sex' }
 * obj = { gender: 'm', name: 'Name'}
 * => { sex: 'm', name: 'Name' }
 */
export const transformFields = (mapFields: TMapFields) => (obj: Record<string, string | null>) => {
    for (const key in mapFields) {
        if (key in obj) {
            obj[mapFields[key]] = obj[key];
            delete obj[key];
        }
    }

    return obj;
};

/**
 * Меняет в объекте местами ключи и значения
 * { a: 'b' } => { b: 'a' }
 */
export const reverseObjectKeysToValue = (mapFields: TMapFields) =>
    Object.keys(mapFields).reduce((acc, key) => ({ ...acc, [mapFields[key]]: key }), {});

/** Оставляет лишь те данные, которые нужно отправлять в архивариус или принимать от него */
export const pickFieldForArchivarius = (data: TResponseFormatData) =>
    pick(data, Array.from(archivariusFields));

/**
 * Для аналитики
 * для тех случаев когда после вызова функции filterEmptyValues получаем пустой объект
 */
export const isEmptyObject = (obj: TFirstStepData) => {
    return Object.keys(obj).length === 0;
};
